import { useState, useCallback } from 'react';
import type { Question, QuestionOption } from '@aphp-ema/common/questionnaire';
import {
  checkQuestionTypeChangeConditions,
  checkQuestionOptionsChangeConditions,
  getRemovedOptions,
  getChangedOptions,
  type BrokenCondition,
} from '@/features/questionnaires/utils/checkQuestionChangeConditions';
type WarningType = 'typeChange' | 'optionChange';

type UseQuestionChangeWarningProps = {
  questions: Question[];
};

export const useQuestionChangeWarning = ({
  questions,
}: UseQuestionChangeWarningProps) => {
  const [showWarning, setShowWarning] = useState(false);
  const [warningType, setWarningType] = useState<WarningType>('typeChange');
  const [brokenConditions, setBrokenConditions] = useState<BrokenCondition[]>(
    [],
  );

  const checkTypeChange = useCallback(
    (questionId: string): BrokenCondition[] | null => {
      const brokenConditionsList = checkQuestionTypeChangeConditions(
        questions,
        questionId,
      );

      if (brokenConditionsList.length > 0) {
        setBrokenConditions(brokenConditionsList);
        setWarningType('typeChange');
        setShowWarning(true);
        return brokenConditionsList;
      }

      return null;
    },
    [questions],
  );

  const checkOptionsChange = useCallback(
    (
      questionId: string,
      oldOptions: QuestionOption[] | null,
      newOptions: QuestionOption[] | null,
    ): BrokenCondition[] | null => {
      const removedOptions = getRemovedOptions(oldOptions, newOptions);
      const changedOptions = getChangedOptions(oldOptions, newOptions);

      if (removedOptions.length === 0 && changedOptions.length === 0) {
        return null;
      }

      const brokenConditionsList = checkQuestionOptionsChangeConditions(
        questions,
        questionId,
        removedOptions,
        changedOptions,
      );

      if (brokenConditionsList.length > 0) {
        setBrokenConditions(brokenConditionsList);
        setWarningType('optionChange');
        setShowWarning(true);
        return brokenConditionsList;
      }

      return null;
    },
    [questions],
  );

  return {
    showWarning,
    warningType,
    brokenConditions,
    checkTypeChange,
    checkOptionsChange,
  };
};
